<template>
  <div class="notification-center">
    <div class="dropdown">
      <button
        ref="toggleBtn"
        class="notification-bell"
        :class="{ 'has-unread': hasUnread, 'has-urgent': urgentCount > 0 }"
        type="button"
        id="notificationDropdown"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
        :title="hasUnread ? `${unreadCount} notification(s) non lue(s)` : 'Notifications'"
        @click="refreshNotifications"
      >
        <i :class="hasUnread ? 'bi bi-bell-fill' : 'bi bi-bell'"></i>
        <span v-if="hasUnread" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </button>

      <div class="dropdown-menu dropdown-menu-end notification-dropdown" aria-labelledby="notificationDropdown">
        <!-- Header -->
        <div class="nc-header">
          <div class="nc-header-top">
            <div>
              <h6 class="nc-title">Notifications</h6>
              <span class="nc-subtitle">
                <template v-if="hasUnread">
                  {{ unreadCount }} non lue{{ unreadCount > 1 ? 's' : '' }}
                  <template v-if="urgentCount"> · <span class="nc-urgent-text">{{ urgentCount }} importante{{ urgentCount > 1 ? 's' : '' }}</span></template>
                </template>
                <template v-else>Vous êtes à jour</template>
              </span>
            </div>
            <div class="nc-header-actions">
              <button
                class="nc-icon-btn"
                :class="{ spin: loading }"
                type="button"
                title="Actualiser"
                @click="refreshNotifications"
              >
                <i class="bi bi-arrow-clockwise"></i>
              </button>
              <button
                v-if="hasUnread"
                class="nc-icon-btn"
                type="button"
                title="Tout marquer comme lu"
                @click="markAllAsRead()"
              >
                <i class="bi bi-check2-all"></i>
              </button>
              <router-link
                to="/settings?notifications=1"
                class="nc-icon-btn"
                title="Paramètres des notifications"
                @click="closeDropdown"
              >
                <i class="bi bi-gear"></i>
              </router-link>
            </div>
          </div>

          <div class="nc-tabs">
            <button type="button" class="nc-tab" :class="{ active: tab === 'all' }" @click="tab = 'all'">
              Toutes <span class="nc-tab-count">{{ notifications.length }}</span>
            </button>
            <button type="button" class="nc-tab" :class="{ active: tab === 'unread' }" @click="tab = 'unread'">
              Non lues <span class="nc-tab-count">{{ unreadCount }}</span>
            </button>
            <button type="button" class="nc-tab" :class="{ active: tab === 'urgent' }" @click="tab = 'urgent'">
              Importantes <span class="nc-tab-count" :class="{ 'nc-tab-count-urgent': urgentCount }">{{ urgentCount }}</span>
            </button>
          </div>

          <div v-if="presentCategories.length > 1" class="nc-chips">
            <button type="button" class="nc-chip" :class="{ active: !category }" @click="category = null">Tout</button>
            <button
              v-for="c in presentCategories"
              :key="c.key"
              type="button"
              class="nc-chip"
              :class="{ active: category === c.key }"
              :style="category === c.key ? { background: c.color, borderColor: c.color } : {}"
              :title="c.label"
              @click="category = category === c.key ? null : c.key"
            >
              <i :class="['bi', c.icon]"></i>
              {{ c.label }}
              <span v-if="c.unread" class="nc-chip-count">{{ c.unread }}</span>
            </button>
          </div>
        </div>

        <!-- List -->
        <div class="nc-list">
          <div v-if="loading && notifications.length === 0" class="nc-empty">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <p>Chargement...</p>
          </div>
          <div v-else-if="displayedNotifications.length === 0" class="nc-empty">
            <div class="nc-empty-icon">
              <i :class="tab === 'all' && !category ? 'bi bi-bell-slash' : 'bi bi-check2-circle'"></i>
            </div>
            <p class="fw-semibold mb-1">{{ emptyTitle }}</p>
            <small>{{ tab === 'all' && !category ? 'Les nouvelles notifications apparaîtront ici.' : 'Rien à signaler dans ce filtre.' }}</small>
          </div>

          <div
            v-for="notification in displayedNotifications"
            :key="notification.id"
            :class="['nc-item', { unread: !notification.read, urgent: notification.priority === 'high' && !notification.read }]"
            role="button"
            tabindex="0"
            @click="openNotification(notification)"
            @keydown.enter="openNotification(notification)"
          >
            <div
              class="nc-item-icon"
              :style="{ color: categoryMeta(notification.category).color, backgroundColor: tint(categoryMeta(notification.category).color) }"
            >
              <i :class="['bi', categoryMeta(notification.category).icon]"></i>
              <span class="nc-item-type" :class="`type-${notification.type}`"></span>
            </div>
            <div class="nc-item-content">
              <div class="nc-item-top">
                <span class="nc-item-title">{{ notification.title || 'Notification' }}</span>
                <span class="nc-item-time">{{ formatTime(notification.timestamp || notification.created_at) }}</span>
              </div>
              <div class="nc-item-message">{{ notification.message }}</div>
              <div class="nc-item-meta">
                <span v-if="notification.priority === 'high'" class="nc-meta-urgent"><i class="bi bi-lightning-charge-fill"></i>Important</span>
                <span v-if="notification.actor"><i class="bi bi-person"></i>{{ notification.actor.name }}</span>
                <span v-if="notification.project" class="nc-meta-project"><i class="bi bi-building"></i>{{ notification.project.name }}</span>
              </div>
              <span v-if="notification.action_url" class="nc-item-more">
                {{ notification.action_label || 'Ouvrir' }} <i class="bi bi-arrow-right-short"></i>
              </span>
            </div>
            <div class="nc-item-side">
              <span v-if="!notification.read" class="nc-unread-dot" title="Non lue"></span>
              <button
                type="button"
                class="nc-item-btn"
                title="Détails"
                @click.stop="showDetail(notification)"
              >
                <i class="bi bi-eye"></i>
              </button>
              <button
                type="button"
                class="nc-item-btn nc-item-remove"
                title="Supprimer"
                @click.stop="removeNotification(notification.id)"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="nc-footer">
          <router-link to="/notifications" class="nc-view-all" @click="closeDropdown">
            Voir toutes les notifications
            <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>

    <NotificationDetailModal
      :notification="selected"
      :has-prev="selectedIndex > 0"
      :has-next="selectedIndex > -1 && selectedIndex < displayedNotifications.length - 1"
      @close="selectedId = null"
      @prev="navigate(-1)"
      @next="navigate(1)"
      @toggle-read="toggleRead"
      @delete="deleteSelected"
      @open-action="openFromDetail"
    />
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Dropdown } from 'bootstrap'
import { useNotifications } from '../composables/useNotifications'
import { syncPushSubscription } from '../utils/pushNotifications'
import NotificationDetailModal from './NotificationDetailModal.vue'

export default {
  name: 'NotificationCenter',
  components: { NotificationDetailModal },
  setup() {
    const {
      notifications,
      unreadCount,
      hasUnread,
      loading,
      catalog,
      initNotifications,
      fetchNotifications,
      categoryMeta,
      startPolling,
      stopPolling,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      removeNotification,
      openAction
    } = useNotifications()

    const router = useRouter()
    const toggleBtn = ref(null)
    const tab = ref('all')
    const category = ref(null)
    const selectedId = ref(null)

    onMounted(() => {
      initNotifications()
      startPolling()
      syncPushSubscription()
    })

    onUnmounted(() => {
      stopPolling()
    })

    const refreshNotifications = () => {
      fetchNotifications()
    }

    const urgentCount = computed(() => notifications.value.filter(n => !n.read && n.priority === 'high').length)

    // Catégories présentes dans les notifications chargées, dans l'ordre du catalogue
    const presentCategories = computed(() => {
      const keys = [...new Set(notifications.value.map(n => n.category || 'general'))]
      const order = catalog.value.map(c => c.key)
      return keys
        .sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
        .map(key => ({
          ...categoryMeta(key),
          key,
          unread: notifications.value.filter(n => (n.category || 'general') === key && !n.read).length
        }))
    })

    const displayedNotifications = computed(() => {
      let list = notifications.value
      if (tab.value === 'unread') list = list.filter(n => !n.read)
      if (tab.value === 'urgent') list = list.filter(n => !n.read && n.priority === 'high')
      if (category.value) list = list.filter(n => (n.category || 'general') === category.value)
      return list.slice(0, 30)
    })

    const emptyTitle = computed(() => {
      if (tab.value === 'unread') return 'Tout est lu !'
      if (tab.value === 'urgent') return 'Rien d\'urgent'
      if (category.value) return 'Aucune notification'
      return 'Aucune notification'
    })

    // Keep the modal bound to the global state so read/unread changes are reflected
    const selected = computed(() => notifications.value.find(n => n.id === selectedId.value) || null)
    const selectedIndex = computed(() => displayedNotifications.value.findIndex(n => n.id === selectedId.value))

    const closeDropdown = () => {
      if (toggleBtn.value) Dropdown.getOrCreateInstance(toggleBtn.value).hide()
    }

    const showDetail = (notification) => {
      selectedId.value = notification.id
      if (!notification.read) markAsRead(notification.id)
      closeDropdown()
    }

    // Clic sur une notification : la page concernée si elle en a une, sinon le détail
    const openNotification = (notification) => {
      closeDropdown()
      if (!openAction(notification, router)) showDetail(notification)
    }

    const openFromDetail = (notification) => {
      selectedId.value = null
      openAction(notification, router)
    }

    const navigate = (step) => {
      const target = displayedNotifications.value[selectedIndex.value + step]
      if (target) {
        selectedId.value = target.id
        if (!target.read) markAsRead(target.id)
      }
    }

    const toggleRead = (notification) => {
      if (notification.read) markAsUnread(notification.id)
      else markAsRead(notification.id)
    }

    const deleteSelected = async (notification) => {
      await removeNotification(notification.id)
      selectedId.value = null
    }

    // Fond translucide de la couleur de catégorie
    const tint = (hex) => `${hex}1f`

    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const now = new Date()
      const time = new Date(timestamp)
      const diff = Math.floor((now - time) / 1000)

      if (diff < 60) return 'À l\'instant'
      if (diff < 3600) return `${Math.floor(diff / 60)} min`
      if (diff < 86400) return `${Math.floor(diff / 3600)} h`
      if (diff < 604800) return `${Math.floor(diff / 86400)} j`

      return time.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    }

    return {
      toggleBtn,
      tab,
      category,
      selectedId,
      selected,
      selectedIndex,
      notifications,
      displayedNotifications,
      presentCategories,
      unreadCount,
      urgentCount,
      hasUnread,
      loading,
      emptyTitle,
      categoryMeta,
      refreshNotifications,
      markAllAsRead,
      removeNotification,
      tint,
      formatTime,
      openNotification,
      openFromDetail,
      showDetail,
      closeDropdown,
      navigate,
      toggleRead,
      deleteSelected
    }
  }
}
</script>

<style scoped>
.notification-center {
  position: relative;
}

.notification-bell {
  position: relative;
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  font-size: 1.15rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-bell:hover {
  background-color: rgba(15, 23, 42, 0.05);
  color: var(--text-main);
}

.notification-bell[aria-expanded="true"] {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color);
}

.notification-bell.has-unread i {
  animation: ring 4s ease-in-out infinite;
  transform-origin: top center;
}

.notification-bell.has-urgent .notification-badge {
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes ring {
  0%, 90%, 100% { transform: rotate(0); }
  92% { transform: rotate(14deg); }
  94% { transform: rotate(-12deg); }
  96% { transform: rotate(8deg); }
  98% { transform: rotate(-4deg); }
}

@keyframes pulse-badge {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55); }
  50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
}

.notification-badge {
  position: absolute;
  top: 3px;
  right: 2px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid var(--bg-card, #fff);
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}

/* Dropdown */
.notification-dropdown {
  position: absolute !important;
  top: 100%;
  right: 0;
  width: 420px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.16);
  margin-top: 10px;
  z-index: 1060;
  overflow: hidden;
  background-color: var(--bg-card);
}

.nc-header {
  padding: 16px 16px 0;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.06), transparent);
}

.nc-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.nc-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-main);
  margin: 0;
}

.nc-subtitle {
  font-size: 12px;
  color: var(--text-muted);
}

.nc-urgent-text {
  color: #ef4444;
  font-weight: 600;
}

.nc-header-actions {
  display: flex;
  gap: 4px;
}

.nc-icon-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.nc-icon-btn:hover {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color);
}

.nc-icon-btn.spin i {
  animation: rotate 0.9s linear infinite;
}

@keyframes rotate {
  to { transform: rotate(360deg); }
}

.nc-tabs {
  display: flex;
  gap: 4px;
  margin-top: 12px;
}

.nc-tab {
  border: none;
  background: none;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
  border-bottom: 2px solid transparent;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.nc-tab:hover {
  color: var(--text-main);
}

.nc-tab.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.nc-tab-count {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.12);
}

.nc-tab.active .nc-tab-count {
  background: rgba(79, 70, 229, 0.12);
}

.nc-tab-count.nc-tab-count-urgent {
  background: rgba(239, 68, 68, 0.14);
  color: #ef4444;
}

/* Category chips */
.nc-chips {
  display: flex;
  gap: 6px;
  padding: 10px 0 12px;
  overflow-x: auto;
  scrollbar-width: none;
}

.nc-chips::-webkit-scrollbar {
  display: none;
}

.nc-chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-card);
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.nc-chip:hover {
  color: var(--text-main);
  border-color: var(--text-muted);
}

.nc-chip.active {
  color: #fff;
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.nc-chip-count {
  font-size: 10.5px;
  font-weight: 700;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
  text-align: center;
}

/* List */
.nc-list {
  max-height: 440px;
  overflow-y: auto;
  padding: 6px;
}

.nc-list::-webkit-scrollbar {
  width: 6px;
}

.nc-list::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.nc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px;
  color: var(--text-muted);
  font-size: 14px;
}

.nc-empty p {
  margin: 8px 0 0;
}

.nc-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.08);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 4px;
}

.nc-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
  border-left: 3px solid transparent;
}

.nc-item + .nc-item {
  margin-top: 2px;
}

.nc-item:hover,
.nc-item:focus-visible {
  background-color: rgba(79, 70, 229, 0.06);
}

.nc-item.unread {
  background-color: rgba(79, 70, 229, 0.04);
}

.nc-item.urgent {
  border-left-color: #ef4444;
}

.nc-item-icon {
  position: relative;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

/* Pastille de gravité (succès, erreur...) sur l'icône de catégorie */
.nc-item-type {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--bg-card, #fff);
}

.nc-item-type.type-success { background: #10b981; }
.nc-item-type.type-error { background: #ef4444; }
.nc-item-type.type-warning { background: #f59e0b; }
.nc-item-type.type-info { background: #3b82f6; }

.nc-item-content {
  flex: 1;
  min-width: 0;
}

.nc-item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}

.nc-item-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-main);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nc-item.unread .nc-item-title {
  font-weight: 700;
}

.nc-item-time {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--text-muted);
}

.nc-item-message {
  font-size: 12.5px;
  color: var(--text-muted);
  line-height: 1.45;
  overflow: hidden;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.nc-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 4px;
  font-size: 11.5px;
  color: var(--text-muted);
}

.nc-item-meta:empty {
  display: none;
}

.nc-item-meta span {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
}

.nc-meta-project {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nc-meta-urgent {
  color: #ef4444;
  font-weight: 600;
}

.nc-item-more {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
}

.nc-item-side {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 24px;
}

.nc-unread-dot {
  width: 9px;
  height: 9px;
  margin: 4px 0 2px;
  border-radius: 50%;
  background: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.nc-item-btn {
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: var(--text-muted);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 12px;
  opacity: 0;
  transition: all 0.15s ease;
}

.nc-item:hover .nc-item-btn,
.nc-item:focus-visible .nc-item-btn {
  opacity: 1;
}

.nc-item-btn:hover {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color);
}

.nc-item-remove:hover {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Footer */
.nc-footer {
  padding: 10px;
  border-top: 1px solid var(--border-color);
}

.nc-view-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.2s ease;
}

.nc-view-all:hover {
  background: rgba(79, 70, 229, 0.08);
}

.nc-view-all i {
  transition: transform 0.2s ease;
}

.nc-view-all:hover i {
  transform: translateX(3px);
}

@media (max-width: 768px) {
  .notification-dropdown {
    position: absolute !important;
    right: -20px !important;
    left: auto !important;
    width: calc(100vw - 32px);
    max-width: 400px;
    margin-top: 1rem !important;
    transform: none !important;
  }

  .nc-item-btn {
    opacity: 1;
  }
}
</style>
